import React from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle2, 
  Download, 
  ArrowRight, 
  Home,
  Settings,
  Megaphone,
  HeartHandshake,
  ExternalLink,
  Clock,
  Sparkles,
  BookOpen,
  Palette,
  Smartphone,
  Accessibility,
  FileText
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from './SEO';

// Tools used in the workshop
const workshopTools = [
  { name: 'Notion', logo: 'https://upload.wikimedia.org/wikipedia/commons/4/45/Notion_app_logo.png', category: 'Operations', url: 'https://notion.so' },
  { name: 'Canva', logo: 'https://customer-assets.emergentagent.com/job_caf6c523-32ed-44de-aaab-8552e94ae126/artifacts/15zeamoo_CP7Z_quEhYkDEAE%3D.webp', category: 'Marketing', url: 'https://canva.com' },
  { name: 'Milanote', logo: 'https://customer-assets.emergentagent.com/job_caf6c523-32ed-44de-aaab-8552e94ae126/artifacts/6sre8ahc_PGX9pn9g_400x400.jpg', category: 'Operations', url: 'https://milanote.com' },
  { name: 'Runway', logo: 'https://customer-assets.emergentagent.com/job_caf6c523-32ed-44de-aaab-8552e94ae126/artifacts/wkx9prw2_eca69951-0148-4dd5-ae85-5a1de75251c6_Runway%2Bsquare.avif', category: 'Marketing', url: 'https://runwayml.com' },
  { name: 'Perplexity', logo: '/assets/perplexity.jpg', category: 'Marketing', url: 'https://perplexity.ai' },
  { name: 'Jotform', logo: 'https://customer-assets.emergentagent.com/job_085bfc62-3b65-48af-bb7c-d17ef39ed429/artifacts/zqtxdnzh_jotformlogo.png', category: 'Customer Service', url: 'https://jotform.com' },
  { name: 'Airtable', logo: 'https://customer-assets.emergentagent.com/job_caf6c523-32ed-44de-aaab-8552e94ae126/artifacts/6if1ce6z_09d47a15534609898b3c26b98664751f.jpg', category: 'Customer Service', url: 'https://airtable.com' },
];

// Workshop recap sections
const recapSections = [
  { 
    icon: Settings, 
    title: 'Operations', 
    time: '10 min',
    summary: 'Build your digital HQ with Notion. Create workspaces, databases, and SOPs to streamline your business operations.'
  },
  { 
    icon: Megaphone, 
    title: 'Marketing', 
    time: '10 min',
    summary: 'Create compelling content with Canva and AI tools. Define your target persona, brand identity, and create video ads.'
  },
  { 
    icon: HeartHandshake, 
    title: 'Customer Service', 
    time: '10 min',
    summary: 'Set up feedback forms with Jotform or Airtable. Create FAQ pages and customer connection systems.'
  },
];

// Data Dreaming resources
const dataDreamingResources = [
  {
    title: 'Free Notion Templates',
    description: 'Business HQ, SOPs, and project management templates',
    icon: FileText,
    status: 'available',
    color: '#E86A4A'
  },
  {
    title: 'Designing Products with Accessibility',
    description: 'Learn inclusive design principles for your products',
    icon: Accessibility,
    status: 'coming-soon',
    color: '#4F46E5'
  },
  {
    title: 'Build a Mobile/Web App MVP in Minutes',
    description: 'From idea to prototype using no-code and AI tools',
    icon: Smartphone,
    status: 'coming-soon',
    color: '#059669'
  },
  {
    title: 'UX Design Fundamentals',
    description: 'Create user-centered experiences for your business',
    icon: Palette,
    status: 'coming-soon',
    color: '#DB2777'
  },
];

const colors = {
  primary: '#E86A4A',
  secondary: '#FDC4E0',
  background: '#221e1e',
  backgroundAlt: '#1a1717',
  pink: '#FDC4E0',
  coral: '#E86A4A',
};

export const WorkshopRecapPage = () => {
  return (
    <div className="min-h-screen" style={{ background: colors.background }}>
      <SEO 
        title="AI for Business Growth - Workshop Recap"
        description="Recap of the AI for Business Growth workshop at Women Business Soirée. Tools, resources, and next steps for your AI journey."
        url="/womenbusinesssoiree/recap"
      />
      {/* Header */}
      <header className="px-4 py-6 border-b border-white/10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors">
            <Home className="w-5 h-5" />
            <span className="text-sm font-medium">Data Dreaming</span>
          </Link>
          <img 
            src="https://customer-assets.emergentagent.com/job_caf6c523-32ed-44de-aaab-8552e94ae126/artifacts/95iizd24_transparent-fortheminds%20%281%29.png" 
            alt="Women Business Soirée" 
            className="h-10 object-contain"
          />
        </div>
      </header>

      {/* Hero Section */}
      <section className="px-4 py-16">
        <motion.div 
          className="max-w-4xl mx-auto text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: `${colors.pink}20` }}>
            <CheckCircle2 className="w-5 h-5" style={{ color: colors.coral }} />
            <span className="text-sm font-medium" style={{ color: colors.pink }}>Workshop Complete!</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            AI for Business Growth
          </h1>
          <p className="text-xl text-white/70 mb-8">
            Your Workshop Recap
          </p>
          
          <p className="text-white/60 max-w-2xl mx-auto">
            Thank you for joining us at the Women Business Soirée! Here's everything we covered, plus resources to continue your AI journey.
          </p>
        </motion.div>
      </section>

      {/* Workshop Recap */}
      <section className="px-4 py-12" style={{ background: colors.backgroundAlt }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">What We Covered</h2>
          
          <div className="space-y-4">
            {recapSections.map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-4 p-6 rounded-2xl"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: colors.pink }}>
                  <section.icon className="w-6 h-6" style={{ color: colors.coral }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-white">{section.title}</h3>
                    <span className="text-xs px-2 py-1 rounded-full bg-white/10 text-white/60">{section.time}</span>
                  </div>
                  <p className="text-white/70 text-sm">{section.summary}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools Used */}
      <section className="px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Tools We Used</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {workshopTools.map((tool, index) => (
              <motion.a
                key={tool.name}
                href={tool.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="flex flex-col items-center p-4 rounded-2xl bg-white/5 hover:bg-white/10 transition-colors group"
              >
                <img src={tool.logo} alt={tool.name} className="w-12 h-12 rounded-xl object-contain mb-3" />
                <span className="text-white font-medium text-sm">{tool.name}</span>
                <span className="text-white/40 text-xs">{tool.category}</span>
                <ExternalLink className="w-4 h-4 text-white/30 mt-2 group-hover:text-white/60 transition-colors" />
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Data Dreaming Resources */}
      <section className="px-4 py-12" style={{ background: colors.backgroundAlt }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <img 
              src="https://customer-assets.emergentagent.com/job_caf6c523-32ed-44de-aaab-8552e94ae126/artifacts/d7ltuzdn_datadreaminglogowhite.png" 
              alt="Data Dreaming" 
              className="h-12 object-contain mx-auto mb-4"
            />
            <h2 className="text-2xl font-bold text-white mb-2">Digital Products & Resources</h2>
            <p className="text-white/60">Continue learning with Data Dreaming</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            {dataDreamingResources.map((resource, index) => (
              <motion.div
                key={resource.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="relative p-6 rounded-2xl overflow-hidden"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                {resource.status === 'coming-soon' && (
                  <div className="absolute top-4 right-4 px-2 py-1 rounded-full text-xs font-medium bg-white/10 text-white/60">
                    Coming Soon
                  </div>
                )}
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${resource.color}20` }}>
                    <resource.icon className="w-6 h-6" style={{ color: resource.color }} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">{resource.title}</h3>
                    <p className="text-white/60 text-sm">{resource.description}</p>
                    
                    {resource.status === 'available' && (
                      <button className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white" style={{ background: resource.color }}>
                        <Download className="w-4 h-4" />
                        Download Free
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 py-16">
        <motion.div 
          className="max-w-2xl mx-auto text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Sparkles className="w-12 h-12 mx-auto mb-6" style={{ color: colors.coral }} />
          <h2 className="text-2xl font-bold text-white mb-4">Stay Connected</h2>
          <p className="text-white/60 mb-8">
            Join the Data Dreaming community for more workshops, resources, and tools to grow your business with AI.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-medium text-white"
              style={{ background: colors.coral }}
            >
              Visit Data Dreaming
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/womenbusinesssoiree"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-medium text-white bg-white/10 hover:bg-white/20 transition-colors"
            >
              Back to Workshop
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="px-4 py-8 border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-white/40 text-sm">
            Women Business Soirée × Data Dreaming × Ocean AI
          </p>
        </div>
      </footer>
    </div>
  );
};

export default WorkshopRecapPage;
